import { Bell, Package, Users } from 'lucide-react';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { Organization, User } from '@/shared/types/userTypes';
import { UsersSection } from '@/domains/accounts/components/tabs/settings-sections/UsersSection';
import { ServiceAccountsSection } from '@/domains/accounts/components/tabs/settings-sections/ServiceAccountsSection';
import { NotificationsSection } from '@/domains/accounts/components/tabs/settings-sections/NotificationsSection';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { OrgType } from '@/shared/types/userRoleTypes';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { cn } from '@/shared/utils/date-utils';
import { useUserRoles } from '@/shared/hooks/userRoles';

interface SettingsTabProps {
  currentUser: User;
  selectedOrganization: Organization;
}

export const SettingsTab = ({ currentUser, selectedOrganization }: SettingsTabProps) => {
  const { hasPermission } = usePermissions();
  const { isTenantUser } = useUserRoles();

  const canAccessServiceAccounts = selectedOrganization.isServiceAccountAllowed === true && hasPermission(OrgType.TENANT, Feature.SERVICE_ACCOUNTS, Permission.READ);
  const canAccessNotifications = isTenantUser && selectedOrganization.isNotificationAllowed === true;
  const tabCount = (canAccessServiceAccounts && canAccessNotifications) ? 3 : (canAccessServiceAccounts || canAccessNotifications) ? 2 : 1;

  return (
    <div className="space-y-4">
      <Tabs defaultValue="users" className="flex flex-col space-y-4">
        <TabsList
          className={cn(
            'grid w-full max-w-xl shrink-0 justify-center mx-auto',
            tabCount === 3 ? 'grid-cols-3' : tabCount === 2 ? 'grid-cols-2' : 'grid-cols-1',
            tabCount === 1 && 'hidden',
          )}
        >
          <TabsTrigger
            value="users"
            className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2 justify-center"
          >
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>

          {canAccessNotifications && (
            <TabsTrigger
              value="notifications"
              className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2 justify-center"
            >
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
          )}

          {canAccessServiceAccounts && (
            <TabsTrigger
              value="service-accounts"
              className="data-[state=active]:bg-foreground data-[state=active]:text-background flex items-center gap-2 justify-center"
            >
              <Package className="h-4 w-4" />
              Service Accounts
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="users">
          <UsersSection currentUser={currentUser} />
        </TabsContent>

        {canAccessNotifications && (
          <TabsContent value="notifications">
            <NotificationsSection />
          </TabsContent>
        )}

        {canAccessServiceAccounts && (
          <TabsContent value="service-accounts">
            <ServiceAccountsSection />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
